class Sidecart extends HTMLElement {
  constructor() {
    super();
    this.firstLoad = true;
    this._enforceRunning = false;
  }

  connectedCallback() {
    this.initEventListeners();
    this.initProgressBar();
    this.initSwipers();
    this.groupCartItems();
    this.setDynamicVH();
  }

  setDynamicVH() {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setVH();
    window.addEventListener("resize", setVH);
    window.addEventListener("orientationchange", setVH);
  }

  initEventListeners() {
    if (this.firstLoad == true) {
      document.querySelectorAll("[cart-toggle]").forEach((button) => {
        button.addEventListener("click", this.toggleCart.bind(this));
        button.addEventListener("keydown", this.toggleCart.bind(this));
      });
    } else {
      this.querySelectorAll("[cart-toggle]").forEach((button) => {
        button.addEventListener("click", this.toggleCart.bind(this));
        button.addEventListener("keydown", this.toggleCart.bind(this));
      });
    }

    // Cadeaux : changement de parfum
    this.querySelectorAll(".gift-variant-select").forEach((select) => {
      select.addEventListener("change", (e) => this.handleGiftVariantChange(e));

      // Auto-select first variant if not in cart
      if (select.dataset.autoSelect === "true" && !select.dataset.itemKey) {
        const selectedOption = select.options[select.selectedIndex];
        if (selectedOption && selectedOption.value) {
          // Trigger auto-add after a short delay to ensure DOM is ready
          setTimeout(() => this.autoAddGiftVariant(select), 100);
        }
      }
    });

    const overlay = document.querySelector(".sidecart__overlay");
    if (overlay) {
      if (this.firstLoad) {
        overlay.addEventListener("click", this.toggleCart.bind(this));
        overlay.addEventListener("keydown", this.toggleCart.bind(this));
      }
    }

    document.querySelectorAll("[data-close-sidecart]").forEach((button) => {
      button.addEventListener("click", this.toggleCart.bind(this));
      button.addEventListener("keydown", this.toggleCart.bind(this));
    });

    // Cart item actions
    this.querySelectorAll('.qty-btn[data-action="increase"]').forEach(
      (button) => {
        button.addEventListener("click", () => this.updateQuantity(button, 1));
      }
    );
    this.querySelectorAll('.qty-btn[data-action="decrease"]').forEach(
      (button) => {
        button.addEventListener("click", () => this.updateQuantity(button, -1));
      }
    );
    this.querySelectorAll(".cart-item__remove").forEach((button) => {
      button.addEventListener("click", () => this.removeItem(button));
    });
    this.querySelectorAll(".suggestion__add").forEach((btn) => {
      btn.addEventListener("click", () => this.addSuggestedItem(btn));
    });

    // Dropdown suggestion: update card on product change
    this.querySelectorAll(".suggestion__dropdown-select").forEach((select) => {
      select.addEventListener("change", (e) => this.handleDropdownChange(e));
    });

    // 👉 Modifier mon pack (redirige vers la fiche bundle en pré-remplissant)
    this.querySelectorAll(".cart-item__modify-btn").forEach((btn) => {
      btn.addEventListener("click", () => this.modifyBundle(btn));
    });

    // Subscription toggles on cart items
    this.initCartSubscriptionToggles();


    // Quand un produit est ajouté ailleurs sur le site
    // -> On réconcilie les cadeaux (ajout auto si 1 seule variante) puis 1 seul refresh.
    // IMPORTANT: Only add this listener once to avoid infinite loops
    if (this.firstLoad == true) {
      document.addEventListener("product:added", async () => {
        try {
          this.showLoading(true);
          await this.reconcileGiftsThenRefreshOnce();
        } catch (error) {
          console.error("Erreur lors de la mise à jour du sidecart:", error);
          this.showError(
            "Une erreur est survenue lors de la mise à jour du panier."
          );
        } finally {
          this.showLoading(false);
        }
      });
    }

    this.firstLoad = false;
  }

  /**
   * Rafraîchit la section “sidecart” (HTML) + ré-initialisations
   * @param {boolean} skipEnforce - si true, n’appelle pas enforceGiftRules dans cette passe
   */
  async refreshCart(skipEnforce = false) {
    try {
      const sectionId = this.dataset.sectionId || "sidecart";
      const response = await fetch(`/?section_id=${sectionId}`);
      if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);

      const lastProgressFillWidth =
        this.querySelector(".sidecart__progress-fill")?.style.width || "0%";
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");
      const newCartContent = doc.querySelector("side-cart");

      if (!newCartContent)
        throw new Error(
          "Impossible de trouver le contenu de la section sidecart."
        );

      this.innerHTML = newCartContent.innerHTML;
      this.updateSummaryTotal?.();
      this.initProgressBar(lastProgressFillWidth);
      this.initEventListeners();
      this.initSwipers();
      this.groupCartItems();

      if (document.querySelector(".quantity_step--container")) {
        const productForm = document.querySelector("product-form");
        if (productForm) {
          await productForm.refreshQuantitySteps();
          await productForm.updateButtonPrice();
        }
      } else {
        this.classList.add("open");
      }

      const headerCount = document.querySelector(".header__cart-count");
      const newCount =
        newCartContent.querySelector(".cart-item-count")?.textContent || "0";
      if (headerCount) headerCount.textContent = newCount;
      document
        .querySelector(".header__cart-count")
        ?.setAttribute("data-cart-count-top", (newCount || "0").trim());

      if (!skipEnforce) {
        await this.enforceGiftRules(); // n'appelle refreshCart qu'en cas de changement
      }

      // Dispatch cart:refreshed event for launch-offer to listen
      document.dispatchEvent(new CustomEvent("cart:refreshed"));
    } catch (error) {
      console.error("Erreur lors du rafraîchissement du panier:", error);
      this.showError(
        "Une erreur est survenue lors de la mise à jour du panier."
      );
    }
  }

  /** Lit et normalise les paliers à partir du <script.sidecart-goals> */
  getGoals(bar) {
    try {
      const cfgEl = bar.querySelector(".sidecart-goals");
      if (!cfgEl) return [];
      const all = JSON.parse(cfgEl.textContent || "[]").filter(
        (g) => g && typeof g.amount !== "undefined"
      );

      all.forEach((g) => {
        g.amount = Number(g.amount) || 0;
        g.product_handle = (g.product_handle || "").toString();
        g.index = Number(g.index) || 0;
      });
      return all.sort((a, b) => a.amount - b.amount);
    } catch (e) {
      console.warn("getGoals parse error", e);
      return [];
    }
  }

  /** Calcule les pourcentages des steps en fonction du nombre de goals */
  getStepPercentages(goalCount) {
    if (goalCount <= 0) return [];
    if (goalCount === 1) return [50];
    if (goalCount === 2) return [38, 75];
    if (goalCount === 3) return [13, 50, 85];
    if (goalCount === 4) return [15, 38, 62, 85];
    // Pour plus de 4, répartition uniforme
    const step = 100 / (goalCount + 1);
    return Array.from({ length: goalCount }, (_, i) => Math.round(step * (i + 1)));
  }

  initProgressBar(lastProgressFillWidth = "0%") {
    const bar = this.querySelector(".sidecart__progress-bar");
    if (!bar) return;

    const total = Number(bar.dataset.cartTotal || 0);
    if (!Number.isFinite(total) || total < 0) return;

    const goals = this.getGoals(bar);
    if (!goals.length) return;

    const finalMessage =
      bar.dataset.finalMessage || "Tous les objectifs atteints 🎉";
    const fill = bar.querySelector(".sidecart__progress-fill");
    const message = bar.querySelector(".sidecart__progress-message");
    const tags = bar.querySelectorAll(".sidecart__tags .tag");

    if (tags.length && tags.length !== goals.length) {
      console.warn("Mismatch goals/tags", goals.length, tags.length);
    }

    const stepAmounts = goals.map((g) => g.amount);
    const stepPercentages = this.getStepPercentages(goals.length);
    const maxGoal = goals[goals.length - 1].amount;

    let progressWidth = "0%";
    let currentStepIndex = stepAmounts.findIndex((amount) => total < amount);
    if (currentStepIndex === -1) currentStepIndex = goals.length;

    if (total >= maxGoal) {
      progressWidth = "100%";
    } else if (total <= 0) {
      progressWidth = "0%";
    } else if (total < stepAmounts[0]) {
      // Progress from 0% to first step percentage
      const firstStepPct = stepPercentages[0];
      const pct = (total / stepAmounts[0]) * firstStepPct;
      progressWidth = `${Math.max(pct, 2)}%`;
    } else {
      const prevAmount = stepAmounts[currentStepIndex - 1];
      const nextAmount = stepAmounts[currentStepIndex] || maxGoal;
      const prevPct = stepPercentages[currentStepIndex - 1];
      const nextPct = stepPercentages[currentStepIndex] || 100;
      const pct =
        prevPct +
        ((total - prevAmount) / (nextAmount - prevAmount)) *
        (nextPct - prevPct);
      progressWidth = `${pct}%`;
    }

    if (fill) {
      fill.style.width = lastProgressFillWidth;
      requestAnimationFrame(() => {
        fill.style.width = progressWidth;
      });
    }

    const nextGoal =
      goals.find((g) => total < g.amount) || goals[goals.length - 1];
    goals.forEach((goal, i) => {
      const tag = tags[i];
      if (!tag) return;

      // Keep completed-from-form class if present (added from launch-offer form)
      const isFromForm = tag.classList.contains("completed-from-form");

      tag.classList.remove("completed", "active", "inactive");

      if (isFromForm || total >= goal.amount) {
        tag.classList.add("completed");
        if (isFromForm) {
          tag.classList.add("completed-from-form");
        }
      } else if (goal.amount === nextGoal.amount) {
        tag.classList.add("active");
      } else {
        tag.classList.add("inactive");
      }
    });

    if (message) {
      if (total < maxGoal) {
        const g = goals.find((g) => total < g.amount);
        const remainingFormatted = window.formatPrice(Math.round((g.amount - total) * 100));
        message.innerHTML = (g.message || "")
          .replace(/\[remaining\]\s*€/, remainingFormatted)
          .replace("[remaining]", remainingFormatted);
      } else {
        message.textContent = finalMessage;
        document.querySelectorAll(".sidecart__tags .tag").forEach((el) => {
          el.classList.remove("active", "inactive");
          el.classList.add("completed");
        });
      }
    }
  }

  /**
   * Récupère /products/{handle}.js
   * @param {string} handle
   * @returns {Promise<{variants: Array, title: string}|null>}
   */
  async fetchProductByHandle(handle) {
    if (!handle) return null;
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        credentials: "same-origin",
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.warn("fetchProductByHandle error", handle, e);
      return null;
    }
  }

  /**
   * Calcule le total du panier sans les cadeaux
   * @param {Object} cart - objet panier depuis /cart.js
   * @returns {number} total en euros
   */
  getCartTotalWithoutGifts(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    const totalCents = cart.items.reduce((sum, item) => {
      // Exclure les items avec la propriété _gift ou le tag gift
      const isGift =
        item?.properties?._gift === "true" ||
        (item?.product_tags || []).includes("gift");

      if (isGift) return sum;

      // Utiliser final_line_price pour prendre en compte les réductions
      return sum + (item.final_line_price || 0);
    }, 0);

    return totalCents / 100;
  }

  /**
   * Applique les règles cadeau SANS rerender :
   * - retire les cadeaux dont le palier n'est plus atteint
   * - ajoute automatiquement les cadeaux à **1 seule variante** si palier atteint et non présents
   * Ne fait pas de refresh; renvoie true si des changements ont été effectués.
   */
  async reconcileGifts() {
    if (this._enforceRunning) return false;
    this._enforceRunning = true;

    try {
      const bar = this.querySelector(".sidecart__progress-bar");
      if (!bar) return false;

      const allGoals = this.getGoals(bar);
      if (!allGoals.length) return false;

      // Single cart fetch for the entire reconciliation
      const cart = await fetch("/cart.js", { credentials: "same-origin" }).then(
        (r) => r.json()
      );
      const totalEuros = this.getCartTotalWithoutGifts(cart);

      const unlockedHandles = new Set(
        allGoals
          .filter((g) => g.product_handle && totalEuros >= g.amount)
          .map((g) => g.product_handle)
      );
      const lockedHandles = new Set(
        allGoals
          .filter((g) => g.product_handle && totalEuros < g.amount)
          .map((g) => g.product_handle)
      );

      const giftLines = (cart.items || []).filter(
        (it) => it?.properties?._gift === "true"
      );
      const presentGiftHandles = new Set(giftLines.map((it) => it.handle));

      let changed = false;

      // 1) Batch-remove ineligible gifts in ONE request
      const updates = {};
      for (const item of giftLines) {
        if (lockedHandles.has(item.handle)) {
          updates[item.key] = 0;
        }
      }
      if (Object.keys(updates).length > 0) {
        await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates }),
        });
        changed = true;
      }

      // 2) Auto-add eligible gifts with exactly 1 available variant if absent
      for (const handle of unlockedHandles) {
        if (presentGiftHandles.has(handle)) continue;

        const prod = await this.fetchProductByHandle(handle);
        if (!prod || !Array.isArray(prod.variants)) continue;

        const availableVariants = prod.variants.filter(
          (v) => !!v && v.available
        );
        if (availableVariants.length !== 1) continue;

        const onlyVariant = availableVariants[0];
        try {
          const randomId =
            Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15);
          await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: Number(onlyVariant.id),
              quantity: 1,
              properties: { _gift: "true", _random: randomId },
            }),
          });
          changed = true;
          // Track locally so we don't re-add if multiple goals share the same handle
          presentGiftHandles.add(handle);
        } catch (e) {
          console.warn("Auto-add gift failed", handle, e);
        }
      }

      return changed;
    } catch (err) {
      console.error("reconcileGifts error:", err);
      return false;
    } finally {
      this._enforceRunning = false;
    }
  }

  /**
   * Variante “historique” appelée par refreshCart() quand on ne cherche pas absolument
   * à limiter à un seul refresh. Elle n’appelle refreshCart(true) **que si** des changements ont été faits.
   */
  async enforceGiftRules() {
    const changed = await this.reconcileGifts();
    if (changed) {
      // Important : on évite une boucle infinie en passant skipEnforce=true
      await this.refreshCart(true);
    }
  }

  /**
   * Pour les cas d’ajout produit (évènement product:added, bouton cross-sell) :
   * on réconcilie d’abord les cadeaux, PUIS on fait un unique refresh.
   */
  async reconcileGiftsThenRefreshOnce() {
    const changed = await this.reconcileGifts();
    // Peu importe changed, on rafraîchit une seule fois l’UI pour refléter le panier à jour
    await this.refreshCart(true);
    return changed;
  }

  handleDropdownChange(e) {
    const select = e.currentTarget;
    const option = select.options[select.selectedIndex];
    if (!option) return;

    const slide = select.closest(".suggestion__item--dropdown");
    if (!slide) return;

    const newImage = option.dataset.image;
    const newName = option.dataset.name;
    const newPrice = option.dataset.price;
    const newHandle = option.dataset.handle;
    const newVariantId = option.dataset.variantId;

    // Update image
    const img = slide.querySelector(".suggestion__image img");
    if (img && newImage) {
      img.src = newImage;
      img.srcset = "";
    }

    // Update name (Cornz logic)
    const isCornz = option.dataset.cornz === "true";
    const nameEl = slide.querySelector(".suggestion__name");
    if (nameEl) nameEl.textContent = isCornz ? "Cornz" : newName;

    // Update price
    const priceEl = slide.querySelector(".suggestion__price--final");
    if (priceEl) priceEl.textContent = newPrice;

    // Update add button variant
    const addBtn = slide.querySelector(".suggestion__add");
    if (addBtn && newVariantId) addBtn.dataset.variantId = newVariantId;

    // Update slide handle
    if (newHandle) slide.dataset.productHandle = newHandle;
  }

  async addSuggestedItem(buttonOrEvent) {
    const variantId = parseInt(
      buttonOrEvent?.dataset?.variantId ?? "123456789",
      10
    );
    const quantity = parseInt(buttonOrEvent?.dataset?.qty ?? "1", 10);
    if (!variantId) return;

    try {
      this.showLoading(true);
      const randomId =
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: variantId,
          quantity,
          properties: { _random: randomId },
        }),
      });

      // Après ajout, on applique la logique “1 refresh” avec cadeaux auto.
      await this.reconcileGiftsThenRefreshOnce();
    } catch (err) {
      console.error(err);
      this.showError("Erreur lors de l’ajout du produit.");
    } finally {
      this.showLoading(false);
    }
  }

  updateSummaryTotal() { }

  toggleCart(event) {
    if (
      event.type === "click" ||
      (event.type === "keydown" && ["Enter", "Space"].includes(event.key))
    ) {
      event.preventDefault();
      this.classList.toggle("open");
    }
  }

  async updateQuantity(button, change) {
    const key = button.dataset.key;
    const variantId = button.dataset.variantId;
    const input = this.querySelector(`.qty-input[data-key="${key}"]`);
    const currentQuantity = parseInt(input.value || input.textContent || 1);
    const newQuantity = currentQuantity + change;
    if (newQuantity < 0) return;

    try {
      this.showLoading(true);

      // Optimistic UI update
      const qtyDisplay = button.closest('.cart-item')?.querySelector('.qty-input.block');
      if (qtyDisplay) qtyDisplay.textContent = newQuantity;

      if (change > 0) {
        if (!variantId) return;

        // Fetch cart to get source item properties + selling plan
        const cart = await fetch("/cart.js", { credentials: "same-origin" }).then(r => r.json());
        const sourceItem = cart.items.find(item => item.key === key);

        const newProperties = {};
        if (sourceItem?.properties) {
          Object.keys(sourceItem.properties).forEach(propKey => {
            if (propKey !== "_random") newProperties[propKey] = sourceItem.properties[propKey];
          });
        }
        newProperties._random = Math.random().toString(36).substring(2, 15);

        const addBody = { id: variantId, quantity: 1, properties: newProperties };
        if (sourceItem?.selling_plan_allocation) {
          addBody.selling_plan = sourceItem.selling_plan_allocation.selling_plan.id;
        }

        await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(addBody),
        });
      } else {
        // For decrement, just remove the specific line item directly
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
      }

      await this.reconcileGiftsThenRefreshOnce();
    } catch (error) {
      console.error("Error updating quantity:", error);
      this.showError("Erreur lors de la mise à jour de la quantité.");
    } finally {
      this.showLoading(false);
    }
  }

  async removeItem(button) {
    try {
      this.showLoading(true);

      const cartItem = button.closest(".cart-item");
      const variantId = cartItem?.dataset.variantId;
      const lineKey = cartItem?.dataset.key;

      if (!variantId) {
        this.showError("Impossible de supprimer cet article.");
        return;
      }

      const cart = await fetch("/cart.js", { credentials: "same-origin" }).then(
        (r) => r.json()
      );

      const targetItem = lineKey
        ? cart.items.find(item => item.key === lineKey)
        : null;

      if (!targetItem) {
        this.showError("Produit introuvable dans le panier.");
        return;
      }

      // Match by variant + properties + selling plan (same logic as groupCartItems)
      const getPropsSignature = (props) => {
        if (!props) return '';
        return Object.keys(props).filter(k => k !== '_random').sort().map(k => `${k}:${props[k]}`).join(';');
      };
      const targetSignature = getPropsSignature(targetItem.properties);
      const targetSellingPlanId = targetItem.selling_plan_allocation?.selling_plan?.id || null;

      const itemsToRemove = cart.items.filter((item) => {
        if (String(item.variant_id) !== String(variantId)) return false;
        if (getPropsSignature(item.properties) !== targetSignature) return false;
        const itemPlanId = item.selling_plan_allocation?.selling_plan?.id || null;
        return itemPlanId === targetSellingPlanId;
      });

      if (itemsToRemove.length === 0) {
        this.showError("Produit introuvable dans le panier.");
        return;
      }

      const updates = {};
      itemsToRemove.forEach((item) => {
        updates[item.key] = 0;
      });

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: response.statusText }));
        throw new Error(
          `Failed to remove items: ${error.message || response.statusText}`
        );
      }

      await this.reconcileGiftsThenRefreshOnce();
    } catch (error) {
      console.error("Error removing item:", error);
      this.showError("Erreur lors de la suppression de l'article.");
    } finally {
      this.showLoading(false);
    }
  }

  // === Nouveau: redirection édition bundle ===
  modifyBundle(button) {
    try {
      const lineKey = button.dataset.key;
      const productUrl = button.dataset.productUrl || "/";
      const bundleVariantId =
        Number(button.dataset.bundleVariant || "0") || null;

      let items = [];
      try {
        items = JSON.parse(button.dataset.bundleJson || "[]");
      } catch (_) {
        items = [];
      }
      const limit = items.reduce((acc, it) => acc + (parseInt(it.qty) || 0), 0);

      const payload = { lineKey, items, limit, bundleVariantId };
      try {
        sessionStorage.setItem("bundle_edit", JSON.stringify(payload));
      } catch (_) { }

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set("edit_bundle", "1");
      window.location.href = url.toString();
    } catch (e) {
      console.error("modifyBundle error", e);
      this.showError("Impossible d’ouvrir le configurateur du pack.");
    }
  }

  showLoading(isLoading) {
    const buttons = this.querySelectorAll(
      ".qty-btn, .cart-item__remove, .suggestion__add, .btn-pay, .cart-item__modify-btn"
    );
    buttons.forEach((button) => {
      button.disabled = isLoading;
      button.style.opacity = isLoading ? "0.5" : "1";
    });
  }

  showError(message) {
    const errorMessage = document.createElement("p");
    errorMessage.textContent = message;
    errorMessage.style.cssText =
      "color: red; text-align: center; margin: 10px 0;";
    errorMessage.setAttribute("aria-live", "assertive");
    this.querySelector(".sidecart__container")?.prepend(errorMessage);
    setTimeout(() => errorMessage.remove(), 5000);
  }

  async handleGiftVariantChange(e) {
    const select = e.currentTarget;
    const variantId = parseInt(select.value, 10);
    if (!variantId || isNaN(variantId)) return;

    const lineKey = select.dataset.itemKey || null;
    const currentVariantId = select.dataset.currentVariantId
      ? parseInt(select.dataset.currentVariantId, 10)
      : null;

    try {
      this.showLoading(true);

      if (lineKey && currentVariantId === variantId) {
        return;
      }

      if (lineKey) {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: lineKey, quantity: 0 }),
        });
      }

      const randomId =
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { _gift: "true", _random: randomId },
        }),
      });

      await this.refreshCart(true);
    } catch (err) {
      console.error("Erreur cadeau:", err);
      this.showError("Impossible d'ajouter le cadeau. Réessayez.");
    } finally {
      this.showLoading(false);
    }
  }

  async autoAddGiftVariant(select) {
    const variantId = parseInt(select.value, 10);
    if (!variantId || isNaN(variantId)) return;

    if (select.dataset.itemKey) return;

    try {
      const randomId =
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { _gift: "true", _random: randomId },
        }),
      });

      await this.refreshCart(true);
    } catch (err) {
      console.error("Auto-add gift error:", err);
    }
  }

  // ⚠️ L'initSwipers d'origine est conservée telle quelle
  initSwipers() {
    this.querySelectorAll(".sidecart__suggestion-swiper").forEach(
      (swiperEl) => {
        if (swiperEl.swiper) {
          swiperEl.swiper.destroy(true, true);
        }
        if (swiperEl.querySelectorAll(".swiper-slide").length > 0) {
          new Swiper(swiperEl, {
            slidesPerView: "auto",
            spaceBetween: 12,
            navigation: {
              nextEl: swiperEl
                .closest(".sidecart__container")
                ?.querySelector(".swiper-button-next"),
              prevEl: swiperEl
                .closest(".sidecart__container")
                ?.querySelector(".swiper-button-prev"),
            },
            pagination: {
              el: swiperEl
                .closest(".sidecart__container")
                ?.querySelector(".swiper-pagination"),
              clickable: true,
            },
          });
        }
      }
    );
  }

  groupCartItems() {
    const cartItems = this.querySelectorAll(".cart-item");
    const groups = new Map();

    cartItems.forEach((item) => {
      const variantId = item.dataset.variantId;
      if (!variantId) return;

      // Group key includes content, selling plan and properties hash
      // so items with different properties or selling plans stay separate
      const content = item.dataset.content || "";
      const sellingPlan = item.dataset.sellingPlanId || "";
      const propsHash = item.dataset.propsHash || "";
      const groupKey = `${variantId}__${content}__${sellingPlan}__${propsHash}`;

      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey).push(item);
    });

    groups.forEach((items, variantId) => {
      if (items.length <= 1) return;

      let totalQuantity = 0;
      let totalFinalPrice = 0;
      let totalOriginalPrice = 0;

      items.forEach((item, index) => {
        const qtyInput = item.querySelector(".qty-input");
        const qty = parseInt(qtyInput?.value || qtyInput?.textContent || 1);
        totalQuantity += qty;

        const priceContainer = item.querySelector(".cart-item__price");
        if (priceContainer) {
          const finalPriceCents = parseInt(
            priceContainer.dataset.finalLinePrice || "0"
          );
          const originalPriceCents = parseInt(
            priceContainer.dataset.originalLinePrice || "0"
          );

          totalFinalPrice += finalPriceCents;
          totalOriginalPrice += originalPriceCents;
        }

        if (index > 0) {
          item.style.display = "none";
        }
      });

      const firstItem = items[0];
      const qtyDisplay = firstItem.querySelector(".qty-input.block");
      if (qtyDisplay) {
        qtyDisplay.textContent = totalQuantity;
      }

      const qtyInputHidden = firstItem.querySelector(".qty-input.hidden");
      if (qtyInputHidden) {
        qtyInputHidden.value = totalQuantity;
      }

      const priceContainer = firstItem.querySelector(".cart-item__price");
      if (priceContainer) {
        const finalPriceEl = priceContainer.querySelector(
          ".cart-item__price--final"
        );
        const comparePriceEl = priceContainer.querySelector(
          ".cart-item__price--compare"
        );

        if (finalPriceEl) {
          finalPriceEl.textContent = window.formatPrice(totalFinalPrice);
        }

        if (totalOriginalPrice > totalFinalPrice) {
          if (!comparePriceEl) {
            const newComparePriceEl = document.createElement("s");
            newComparePriceEl.className = "cart-item__price--compare";
            priceContainer.insertBefore(newComparePriceEl, finalPriceEl);
          }
          const compareEl = priceContainer.querySelector(
            ".cart-item__price--compare"
          );
          if (compareEl) {
            compareEl.textContent = window.formatPrice(totalOriginalPrice);
            compareEl.style.display = "";
          }
        } else if (comparePriceEl) {
          comparePriceEl.style.display = "none";
        }
      }
    });
  }

  initCartSubscriptionToggles() {
    this.querySelectorAll('[data-cart-item-sub]').forEach(sub => {
      const toggle = sub.querySelector('[data-sub-toggle]');
      const tabs = sub.querySelectorAll('.cart-item-sub__tab');
      const lineKey = sub.dataset.lineKey;
      const variantId = sub.dataset.variantId;
      const productId = sub.dataset.productId || '';
      const collectionId = sub.dataset.collectionId || '';
      const quantity = sub.dataset.quantity || '1';
      const plansDataEl = sub.querySelector('[data-cart-sub-plans]');
      if (!toggle || !plansDataEl) return;

      let plansData;
      try {
        plansData = JSON.parse(plansDataEl.textContent);
      } catch(e) { return; }

      toggle.addEventListener('click', () => {
        const isActive = toggle.classList.contains('is-active');
        if (isActive) {
          // Switching to one-time — still pass first plan for _rb_id generation
          this.updateCartItemSellingPlan(lineKey, '', variantId, productId, collectionId, plansData);
        } else {
          const firstPlan = plansData.plans[0];
          if (firstPlan) {
            this.updateCartItemSellingPlan(lineKey, firstPlan.id, variantId, productId, collectionId, plansData);
          }
        }
      });

      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const planId = tab.dataset.subPlanId;
          this.updateCartItemSellingPlan(lineKey, planId, variantId, productId, collectionId, plansData);
        });
      });
    });
  }

  async updateCartItemSellingPlan(lineKey, sellingPlanId, variantId, productId, collectionId, plansData) {
    this.showLoading(true);
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      const sourceItem = cart.items.find(item => item.key === lineKey);
      if (!sourceItem) throw new Error('Item not found in cart');

      const getPropsSignature = (props) => {
        if (!props) return '';
        return Object.keys(props).filter(k => k !== '_random').sort().map(k => `${k}:${props[k]}`).join(';');
      };
      const sourceSignature = getPropsSignature(sourceItem.properties);
      const sourceSellingPlan = sourceItem.selling_plan_allocation?.selling_plan?.id || null;

      const groupedItems = cart.items.filter(item => {
        if (String(item.variant_id) !== String(variantId)) return false;
        if (getPropsSignature(item.properties) !== sourceSignature) return false;
        const itemPlan = item.selling_plan_allocation?.selling_plan?.id || null;
        return itemPlan === sourceSellingPlan;
      });

      // Generate _rb_id via Recharge SDK if product has selling plans
      let rbId = '';
      let updatedBundleJson = '';
      console.log('[Recharge Sidecart] === START updateCartItemSellingPlan ===');
      console.log('[Recharge Sidecart] lineKey:', lineKey, 'sellingPlanId:', sellingPlanId, 'variantId:', variantId, 'productId:', productId);
      console.log('[Recharge Sidecart] sourceItem properties:', JSON.stringify(sourceItem.properties, null, 2));
      console.log('[Recharge Sidecart] plansData:', JSON.stringify(plansData, null, 2));
      console.log('[Recharge Sidecart] recharge available:', typeof recharge !== 'undefined', 'recharge.bundle:', typeof recharge !== 'undefined' && !!recharge.bundle);

      if (productId && typeof recharge !== 'undefined' && recharge.bundle) {
        try {
          const bundleSellingPlanId = sellingPlanId || (plansData?.plans?.[0]?.id ? String(plansData.plans[0].id) : '');
          console.log('[Recharge Sidecart] bundleSellingPlanId:', bundleSellingPlanId);

          // Try to reconstruct bundle from stored selections
          const storedSelections = sourceItem.properties?._rb_selections;
          console.log('[Recharge Sidecart] storedSelections:', storedSelections ? 'YES' : 'NO');
          let bundle;

          if (storedSelections) {
            // Parse stored bundle and match selling plans per flavor by name
            bundle = JSON.parse(storedSelections);
            const targetPlan = plansData?.plans?.find(p => String(p.id) === String(bundleSellingPlanId));
            const targetPlanName = targetPlan?.name || '';
            const rawFlavorPlans = sourceItem.properties?._flavor_plans;
            const flavorPlans = rawFlavorPlans ? JSON.parse(rawFlavorPlans) : {};
            console.log('[Recharge Sidecart] targetPlanName:', targetPlanName);
            console.log('[Recharge Sidecart] _flavor_plans raw:', rawFlavorPlans);
            console.log('[Recharge Sidecart] flavorPlans parsed:', JSON.stringify(flavorPlans, null, 2));

            bundle.selections.forEach((sel, i) => {
              if (targetPlanName && flavorPlans[sel.externalVariantId]) {
                const match = flavorPlans[sel.externalVariantId].find(p => p.name === targetPlanName);
                console.log(`[Recharge Sidecart] sel[${i}] variantId:${sel.externalVariantId} match:`, match ? match.id : 'NONE, fallback to bundle plan');
                sel.sellingPlan = match ? String(match.id) : String(bundleSellingPlanId);
              } else {
                console.log(`[Recharge Sidecart] sel[${i}] variantId:${sel.externalVariantId} no flavorPlans, using bundleSellingPlanId`);
                sel.sellingPlan = String(bundleSellingPlanId);
              }
            });
            console.log('[Recharge Sidecart] Rebuilt bundle from _rb_selections');
          } else {
            // Fallback: simple single-selection bundle
            const totalQty = groupedItems.reduce((sum, item) => sum + item.quantity, 0);
            bundle = {
              externalProductId: String(productId),
              externalVariantId: String(variantId),
              selections: [{
                externalProductId: String(productId),
                externalVariantId: String(variantId),
                collectionId: String(collectionId || ''),
                quantity: totalQty,
                sellingPlan: String(bundleSellingPlanId)
              }]
            };
          }

          console.log('[Recharge Sidecart] bundle:', JSON.stringify(bundle, null, 2));

          const isValid = await recharge.bundle.validateBundle(bundle);
          console.log('[Recharge Sidecart] validateBundle:', isValid);

          const { valid, error } = await recharge.bundle.validateBundleSelection(bundle);
          console.log('[Recharge Sidecart] validateBundleSelection:', valid, error);

          if (isValid && valid) {
            rbId = await recharge.bundle.getBundleId(bundle);
            updatedBundleJson = JSON.stringify(bundle);
            console.log('[Recharge Sidecart] _rb_id:', rbId);
          }
        } catch(e) {
          console.error('[Recharge Sidecart] Erreur getBundleId:', e);
        }
      }

      // Build items array for a single batch add
      const itemsToAdd = groupedItems.map(item => {
        const cleanProps = {};
        const existingRbId = item.properties?._rb_id || '';
        const existingSelections = item.properties?._rb_selections || '';
        if (item.properties) {
          Object.keys(item.properties).forEach(key => {
            if (key !== '_random' && key !== '_rb_id' && key !== '_rb_selections') cleanProps[key] = item.properties[key];
          });
        }
        // Use new _rb_id if available, otherwise keep existing
        const finalRbId = rbId || existingRbId;
        if (finalRbId) cleanProps._rb_id = finalRbId;
        // Store updated bundle selections (or keep existing)
        const finalSelections = updatedBundleJson || existingSelections;
        if (finalSelections) cleanProps._rb_selections = finalSelections;
        cleanProps._random = Math.random().toString(36).substring(2, 15);
        const addItem = { id: parseInt(variantId), quantity: item.quantity, properties: cleanProps };
        if (sellingPlanId) addItem.selling_plan = parseInt(sellingPlanId);
        return addItem;
      });

      // Remove old items first, then add new ones (sequential to avoid race conditions)
      const updates = {};
      groupedItems.forEach(item => { updates[item.key] = 0; });

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
        credentials: 'same-origin'
      });

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd }),
        credentials: 'same-origin'
      });
      console.log('[Recharge Sidecart] add response status:', addResponse.status, await addResponse.clone().text());

      await this.refreshCart(true);
    } catch(error) {
      console.error('Error updating selling plan:', error);
      await this.refreshCart(true);
    } finally {
      this.showLoading(false);
    }
  }
}

customElements.define("side-cart", Sidecart);
